import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { unionBy } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';

import { createdBy } from '~components/Job/AuditDetails/utils';
import { OutboundEmail } from '~hooks/useOutboundEmails/models';
import useSettlementOutboundEmails from '~hooks/useOutboundEmails/useSettlementOutboundEmails';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { getOutboundEmailRecipientStatusInfo } from './SettlementOutboundEmailStatus';

interface SettlementOutboundEmailsProps {
  lastOutboundEmail: OutboundEmail | null;
  settlementId: string;
}

const TableRowStyled = styled(TableRow)<TableRowProps>(({ theme }) => ({
  '&:not(.MuiTableBody-root tr:last-child)': {
    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  },
}));

const TableCellStyled = styled(TableCell, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<TableCellProps & { isHeader?: boolean }>(({ isHeader, theme }) => ({
  borderBottom: 0,
  color: theme.brandV2.colors.treadBlack,
  fontSize: isHeader ? '11px' : '12px',
  fontWeight: isHeader ? 600 : 400,
  padding: theme.spacing(1),
  whiteSpace: 'nowrap',
}));

const SettlementOutboundEmails = ({
  lastOutboundEmail,
  settlementId,
}: SettlementOutboundEmailsProps) => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getSettlementOutboundEmails } = useSettlementOutboundEmails();
  const [outboundEmails, setOutboundEmails] = useState<OutboundEmail[]>([]);
  const [nextPageCursor, setNextPageCursor] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const firstPageFetchCount = useRef(0);

  useEffect(() => {
    let isCurrent = true;
    firstPageFetchCount.current += 1;

    const fetchOutboundEmails = async () => {
      setIsLoading(true);
      try {
        const page = await getSettlementOutboundEmails(companyId, settlementId);

        if (isCurrent) {
          setOutboundEmails(page.data);
          setNextPageCursor(page.pagination.after);
        }
      } catch {
        // The hook already reported it.
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    void fetchOutboundEmails();

    return () => {
      isCurrent = false;
    };
  }, [
    companyId,
    getSettlementOutboundEmails,
    lastOutboundEmail?.id,
    lastOutboundEmail?.state,
    settlementId,
  ]);

  const handleLoadMore = useCallback(async () => {
    const fetchCountWhenStarted = firstPageFetchCount.current;

    setIsLoadingMore(true);
    try {
      const page = await getSettlementOutboundEmails(
        companyId,
        settlementId,
        'after',
        nextPageCursor,
      );

      if (fetchCountWhenStarted !== firstPageFetchCount.current) {
        return;
      }

      setOutboundEmails((loaded) => unionBy(loaded, page.data, 'id'));
      setNextPageCursor(page.pagination.after);
    } catch {
      // The hook already reported it.
    } finally {
      setIsLoadingMore(false);
    }
  }, [companyId, getSettlementOutboundEmails, nextPageCursor, settlementId]);

  const recipientRows = outboundEmails.flatMap((email) =>
    email.recipients.map((recipient) => ({ email, recipient })),
  );

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.semantic.border}`,
        borderRadius: '6px',
        backgroundColor: (theme) => theme.semantic.surfaceRaised,
        p: 2,
        minWidth: '300px',
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          mb: 1,
        }}
      >
        {t('approvals.driver_pay.settlements.settlements_email_deliveries.title')}
      </Typography>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : recipientRows.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t(
            'approvals.driver_pay.settlements.settlements_email_deliveries.no_deliveries',
          )}
        </Typography>
      ) : (
        <TableContainer sx={{ overflow: 'auto' }}>
          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRowStyled>
                <TableCellStyled isHeader>
                  {t(
                    'approvals.driver_pay.settlements.settlements_email_deliveries.recipient',
                  )}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t(
                    'approvals.driver_pay.settlements.settlements_email_deliveries.sent_at',
                  )}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t(
                    'approvals.driver_pay.settlements.settlements_email_deliveries.sent_by',
                  )}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t(
                    'approvals.driver_pay.settlements.settlements_email_deliveries.status',
                  )}
                </TableCellStyled>
              </TableRowStyled>
            </TableHead>

            <TableBody>
              {recipientRows.map(({ email, recipient }) => {
                const { icon, label } = getOutboundEmailRecipientStatusInfo(
                  recipient.state,
                );

                return (
                  <TableRowStyled key={`${email.id}-${recipient.id}`}>
                    <TableCellStyled>{recipient.email}</TableCellStyled>
                    <TableCellStyled>
                      {dayjs.tz(email.createdAt).format('MMM DD, YYYY hh:mm a')}
                    </TableCellStyled>
                    <TableCellStyled>
                      {email.sentBy ? createdBy(email.sentBy) : '-'}
                    </TableCellStyled>
                    <TableCellStyled>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {icon}
                        <Typography variant="body2">{label}</Typography>
                      </Box>
                    </TableCellStyled>
                  </TableRowStyled>
                );
              })}
            </TableBody>
          </Table>
          {nextPageCursor && (
            <Button
              loading={isLoadingMore}
              onClick={handleLoadMore}
              size="small"
              sx={{ mt: 1 }}
            >
              {t('actions.load_more')}
            </Button>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default SettlementOutboundEmails;
